// Thin fetch wrapper. A pasted token lives in localStorage (per-browser
// convenience); signing in with GitHub sets an HttpOnly session
// cookie instead, which same-origin requests carry. The server can also allow
// anonymous read-only browsing (BEE_UI_PUBLIC=1).

import type { ApiError } from "./types";

const TOKEN_KEY = "bee_token";

export const token = {
  get(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(v: string | null) {
    try {
      if (v) localStorage.setItem(TOKEN_KEY, v);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* storage unavailable: token lasts for this page only */
    }
  },
};

/** Whether a GitHub session cookie exists: the server sets this readable marker beside it. */
export const hasSession = () => document.cookie.split("; ").includes("bee_signed_in=1");

/** Ends the session (if any) and forgets the pasted token. */
export async function signOut() {
  token.set(null);
  await fetch("/auth/logout", { method: "POST" }).catch(() => undefined);
}

export class HttpError extends Error {
  readonly status: number;
  readonly body: ApiError;
  constructor(status: number, body: ApiError) {
    super(body.message);
    this.status = status;
    this.body = body;
  }
}

// crypto.randomUUID exists only in secure contexts, and the UI is often served over
// plain HTTP on a LAN or *.localtest.me host; getRandomValues works everywhere.
function uuid(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Repo names are unique per owner, so a request for the repo of the page being viewed says whose it is. */
function pageOwner(path: string): string | undefined {
  const m = /^\/v1\/repos\/([^/?#]+)/.exec(path);
  if (!m) return undefined;
  const [, owner = "", repo = ""] = location.pathname.split("/");
  return owner && owner !== "_" && !TOP.has(owner) && decodeURIComponent(repo) === m[1] ? decodeURIComponent(owner) : undefined;
}

function headers(path: string, extra: Record<string, string> = {}): Record<string, string> {
  const h: Record<string, string> = { ...extra };
  const t = token.get();
  if (t) h.authorization = `Bearer ${t}`;
  const owner = pageOwner(path);
  if (owner) h["bee-owner"] = owner;
  return h;
}

/** An error response's body; a proxy or a crashed handler can answer with no JSON at all. */
async function failure(res: Response): Promise<HttpError> {
  const body = (await res.json().catch(() => ({ status: "error", code: "HTTP", message: `${res.status} ${res.statusText || "empty response"} from ${res.url ? new URL(res.url).pathname : "the server"}`, hint: "" }))) as ApiError;
  return new HttpError(res.status, body);
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) throw await failure(res);
  const text = await res.text();
  return (text ? JSON.parse(text) : { status: "ok" }) as T;
}

export function qs(params: Record<string, string | number | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const api = {
  // A request the server never answers (an exhausted DB pool) would leave the page on its loading skeleton forever; time out so it shows an error and retries.
  get: <T>(path: string) => fetch(path, { headers: headers(path), signal: AbortSignal.timeout(30_000) }).then((r) => handle<T>(r)),
  text: (path: string) =>
    fetch(path, { headers: headers(path) }).then(async (r) => {
      if (!r.ok) throw await failure(r);
      return r.text();
    }),
  bytes: (path: string) =>
    fetch(path, { headers: headers(path) }).then(async (r) => {
      if (!r.ok) throw await failure(r);
      return { body: new Uint8Array(await r.arrayBuffer()), headers: r.headers };
    }),
  /** Saves a response as a file. Downloads need the bearer token, so a plain link will not do. */
  download: async (path: string, filename: string) => {
    const r = await fetch(path, { headers: headers(path) });
    if (!r.ok) throw await failure(r);
    const url = URL.createObjectURL(await r.blob());
    const a = Object.assign(document.createElement("a"), { href: url, download: filename });
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },
  send: <T>(method: string, path: string, body: unknown) =>
    fetch(path, {
      method,
      headers: headers(path, { "content-type": "application/json", "idempotency-key": uuid() }),
      body: JSON.stringify(body),
    }).then((r) => handle<T>(r)),
};

export const repoPath = (repo: string, rest = "") => `/v1/repos/${repo}${rest}`;

/** Owner of each repo seen so far, so a link can be built from a bare repo name. */
const owners = new Map<string, string>();
export const rememberOwner = (repo: string, owner?: string | null) => {
  if (owner) owners.set(repo, owner);
};

/** A repo's page: `/owner/slug`. The owner is cached from the URL or an API row; `_` only covers a repo not seen yet, which the server then finds by its key. */
export const rp = (repo: string, owner?: string | null) => `/${encodeURIComponent(owner || owners.get(repo) || "_")}/${repo}`;

const TOP = new Set(["", "orgs", "new", "login", "profile", "oauth", "device", "admin", "account", "org"]);

/** The repo a URL is inside (`/owner/repo/...`, or a legacy `/repo/...`), or "" on a top-level page. */
export function repoFromPath(pathname: string): string {
  const [, first = "", second = ""] = pathname.split("/");
  return TOP.has(first) ? "" : decodeURIComponent(second || first);
}
